import { useQuery } from '@tanstack/react-query';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { api } from '@/lib/api';

interface HealthHistoryRow {
  id: number;
  total_score: number;
  grade: string;
  captured_at: string;
}

/**
 * rpt_ : HealthTimelineChart — line chart of account health history.
 * Used by AccountDetailPage to visualise score trends over 90 days.
 */
export function HealthTimelineChart({ accountId, days = 90 }: { accountId: number; days?: number }) {
  const { data, isLoading } = useQuery<{ data: HealthHistoryRow[] }>({
    queryKey: ['health-history', accountId, days],
    queryFn: async () => (await api.get(`/accounts/${accountId}/health-history?days=${days}`)).data,
    enabled: Boolean(accountId),
  });

  const rows = data?.data ?? [];

  const chartData = rows.map((r) => ({
    date: new Date(r.captured_at).toLocaleDateString('pl-PL', {
      day: 'numeric',
      month: 'short',
    }),
    score: r.total_score,
  }));

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold dark:text-white">Historia Health Score</h3>
        <span className="text-xs text-slate-500">Ostatnie {days} dni</span>
      </div>
      {isLoading ? (
        <div className="h-[200px] flex items-center justify-center text-xs text-slate-400">
          Ładowanie…
        </div>
      ) : rows.length === 0 ? (
        <div className="h-[200px] flex items-center justify-center text-xs text-slate-400">
          Brak historii — uruchom `php artisan aiads:snapshot-health-scores`.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={Math.ceil(chartData.length / 10)} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} width={32} />
            <Tooltip
              contentStyle={{
                background: '#ffffff',
                border: '1px solid #E2E8F0',
                borderRadius: '10px',
                fontSize: '12px',
              }}
            />
            <Line
              type="monotone"
              dataKey="score"
              stroke="#2E5BFF"
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
